import { DragEndEvent } from '@dnd-kit/core';

import { ColumnType, KanbanColumn, KanbanMove } from '@/models/RoomView.ts';
import { DailyAppointment } from '@/models/appointment/Appointment.ts';
import { EnrichedPractitionerData } from '@/models/appointment/EnrichedPractitionerData.ts';
import { AppointmentMoveOutcome } from '@/models/appointment/appointment-move-outcome.ts';
import { CheckinIntent, CheckinOutcomeKind } from '@/models/call-system/check-in.ts';
import { UserAppointmentDto } from '@/models/call-system/dtos/user-appointment-dto.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';
import { DCTaskStatus } from '@/models/doc-cirrus/DCTask.ts';
import FormattedForm from '@/models/forms/FormattedForm.ts';
import { CheckinEntry } from '@/models/metrics.ts';

import {
    checkin,
    checkoutAppointment,
    moveAppointmentToExpected,
    prepareAndCheckinArrived,
    reportCheckinCompletion,
    updateAppointment,
} from '@/services/appointmentService.ts';
import { getRequiredAppointmentForms } from '@/services/formDataService.ts';
import { showErrorNotification } from '@/services/notificationService.ts';

import {
    appendPatientContext,
    getInstanceId,
    isCheckedIn,
    isCheckedOut,
    isCheckinArrived,
    isEternoAppointment,
} from '@/utils/appointmentUtils.ts';
import { getSelectedLocation } from '@/utils/configUtils.ts';

export interface OnFailOptions {
    notifyFailedMove?: boolean;
    checkInFailedAfterMove?: boolean;
}

export interface DragAndDropHandlers {
    onSuccess: () => void;
    onFail: (options?: OnFailOptions) => void;
    setAppointmentCheckingIn: (appointmentId: string, isCheckingIn: boolean) => void;
    mutateCheckinData: () => Promise<unknown>;
    mutateAppointmentsForDate: () => Promise<unknown>;
    acquireCheckinLock?: () => void;
}

export const moveFailedMessage = (appointment: DailyAppointment): string =>
    appendPatientContext('Der Termin konnte nicht verschoben werden.', appointment);

// Resolves forms for the check-in code; callers that already have them should pass them in.
const fetchFormsForAppointment = async (appointment: DailyAppointment): Promise<FormattedForm[] | undefined> => {
    const requiredForms = await getRequiredAppointmentForms(appointment, getSelectedLocation());
    return requiredForms.formatted_forms_for_appointment;
};

export const handleAppointmentMove = async (
    appointment: DailyAppointment,
    source_column: KanbanColumn<ColumnType, DailyAppointment>,
    destination_column: KanbanColumn<ColumnType, DailyAppointment>,
    checkin_appointment: UserAppointmentDto | undefined,
    enriched_practitioner_data: EnrichedPractitionerData | undefined,
    handlers: DragAndDropHandlers
): Promise<void> => {
    // Moved to treatment room
    if (destination_column.type === ColumnType.TREATMENT_ROOM) {
        if (!validateMoveAndNotify(handlers, destination_column, appointment)) return;

        await handleCheckinMove(
            appointment,
            destination_column.id,
            checkin_appointment,
            enriched_practitioner_data,
            handlers,
            'drag'
        );
        return;
    }

    // Room to room change
    if (isRoomColumn(source_column) && isRoomColumn(destination_column)) {
        if (!validateMoveAndNotify(handlers, destination_column, appointment)) return;

        await handleRoomToRoomMove(appointment, destination_column, handlers);
        return;
    }

    // Arrived
    if (
        (source_column.type === ColumnType.EXPECTED || source_column.type === ColumnType.DISCHARGED) &&
        isRoomColumn(destination_column)
    ) {
        if (!validateMoveAndNotify(handlers, destination_column, appointment)) return;

        await handleArrivedMove(
            appointment,
            destination_column,
            checkin_appointment,
            enriched_practitioner_data,
            handlers,
            'drag'
        );
        return;
    }

    // checkout
    if (isRoomColumn(source_column) && destination_column.type === ColumnType.DISCHARGED) {
        await handleCheckoutMove(appointment, checkin_appointment, handlers);
        return;
    }

    // move to expected
    if (
        (source_column.type === ColumnType.DISCHARGED || isRoomColumn(source_column)) &&
        destination_column.type === ColumnType.EXPECTED
    ) {
        await handleMoveToExpected(appointment, checkin_appointment, handlers);
        return;
    }

    // reset statuses if no early return
    handlers.onFail();
};

export const handleCheckinMove = async (
    appointment: DailyAppointment,
    destination_room: string,
    checkin_appointment: UserAppointmentDto | undefined,
    enriched_practitioner_data: EnrichedPractitionerData | undefined,
    handlers: DragAndDropHandlers,
    entry: CheckinEntry,
    forms_for_appointment?: FormattedForm[]
): Promise<void> => {
    const ticket = checkin_appointment?.number;

    const should_arrive =
        !isCheckinArrived(checkin_appointment?.appointment?.checkin_status) &&
        !isCheckedIn(checkin_appointment?.appointment?.checkin_status);

    // The service owns the single check-in write and reports the outcome; we only react to it.
    if (should_arrive) {
        const practitioner = enriched_practitioner_data?.practitioners.find(
            (prac) => prac.id === appointment.professional.id
        );
        handlers.acquireCheckinLock?.();
        const forms = forms_for_appointment ?? (await fetchFormsForAppointment(appointment));
        const outcome = await prepareAndCheckinArrived({
            appointment: appointment,
            selected_room: destination_room,
            forms_for_appointment: forms,
            practitioner: practitioner,
            intent: CheckinIntent.CHECK_IN,
            existing_ticket: checkin_appointment?.number,
            entry: entry,
        });
        if (outcome.kind === CheckinOutcomeKind.FAILED || outcome.kind === CheckinOutcomeKind.CANCELLED) {
            handlers.onFail();
        } else {
            handlers.onSuccess();
        }
        return;
    }

    // Already arrived/checked-in: move to the destination room. When the move also does the check-in write
    // it completes an arrive→confirm flow and reports the outcome; a plain room change reports nothing.
    const completesCheckin = Boolean(
        ticket &&
        (!isCheckedIn(checkin_appointment?.appointment?.checkin_status) ||
            isCheckedOut(checkin_appointment?.appointment?.checkout_status))
    );

    const [update_room_success] = await updateAppointment(appointment, {
        appointment: { room_id: destination_room },
    });
    if (!update_room_success) {
        if (completesCheckin) {
            reportCheckinCompletion(appointment.id, entry, {
                kind: CheckinOutcomeKind.FAILED,
                reason: 'room_update_failed',
            });
        }
        handlers.onFail({ notifyFailedMove: true });
        return;
    }
    void handlers.mutateAppointmentsForDate();

    if (completesCheckin && ticket) {
        const checkin_success = await checkin(appointment, ticket);
        reportCheckinCompletion(
            appointment.id,
            entry,
            checkin_success
                ? { kind: CheckinOutcomeKind.CHECKED_IN, ticket: ticket }
                : { kind: CheckinOutcomeKind.FAILED, reason: 'checkin_call_failed' }
        );
        if (checkin_success) {
            handlers.onSuccess();
        } else {
            await handleCheckInFailureAfterMove(handlers);
        }
        return;
    }

    handlers.onSuccess();
};

export const handleRoomToRoomMove = async (
    appointment: DailyAppointment,
    destination_column: KanbanColumn<ColumnType, DailyAppointment>,
    handlers: DragAndDropHandlers
): Promise<void> => {
    const [update_room_success] = await updateAppointment(appointment, {
        appointment: { room_id: destination_column.id },
    });
    if (!update_room_success) {
        handlers.onFail({ notifyFailedMove: true });
        return;
    }
    handlers.onSuccess();
};

export const handleArrivedMove = async (
    appointment: DailyAppointment,
    destination_column: KanbanColumn<ColumnType, DailyAppointment>,
    checkin_appointment: UserAppointmentDto | undefined,
    enriched_practitioner_data: EnrichedPractitionerData | undefined,
    handlers: DragAndDropHandlers,
    entry: CheckinEntry,
    forms_for_appointment?: FormattedForm[]
): Promise<void> => {
    const practitioner = enriched_practitioner_data?.practitioners.find(
        (prac) => prac.id === appointment.professional.id
    );

    handlers.acquireCheckinLock?.();
    const forms = forms_for_appointment ?? (await fetchFormsForAppointment(appointment));
    const outcome = await prepareAndCheckinArrived({
        appointment: appointment,
        selected_room: destination_column.id,
        forms_for_appointment: forms,
        practitioner: practitioner,
        intent: CheckinIntent.ARRIVE,
        existing_ticket: checkin_appointment?.number,
        entry: entry,
    });
    if (outcome.kind === CheckinOutcomeKind.FAILED || outcome.kind === CheckinOutcomeKind.CANCELLED) {
        handlers.onFail();
    } else {
        handlers.onSuccess();
    }
};

export const handleCheckoutMove = async (
    appointment: DailyAppointment,
    checkin_appointment: UserAppointmentDto | undefined,
    handlers: DragAndDropHandlers
): Promise<void> => {
    const outcome = await checkoutAppointment(appointment, checkin_appointment?.number ?? '');
    await resolveMoveOutcome(outcome, handlers);
};

export const handleMoveToExpected = async (
    appointment: DailyAppointment,
    checkin_appointment: UserAppointmentDto | undefined,
    handlers: DragAndDropHandlers
): Promise<void> => {
    const outcome = await moveAppointmentToExpected(appointment, checkin_appointment?.number ?? '');
    await resolveMoveOutcome(outcome, handlers);
};

const handleCheckInFailureAfterMove = async (handlers: DragAndDropHandlers): Promise<void> => {
    // Await both revalidations before releasing the card, so it can't be re-dragged on stale state.
    await Promise.all([handlers.mutateAppointmentsForDate(), handlers.mutateCheckinData()]);
    handlers.onFail({ checkInFailedAfterMove: true });
};

const resolveMoveOutcome = async (outcome: AppointmentMoveOutcome, handlers: DragAndDropHandlers): Promise<void> => {
    if (outcome === AppointmentMoveOutcome.SUCCESS) {
        handlers.onSuccess();
        return;
    }
    if (outcome === AppointmentMoveOutcome.PARTIAL) {
        // Room change already persisted; await revalidations before releasing the move to avoid a stale re-drag.
        await Promise.all([handlers.mutateAppointmentsForDate(), handlers.mutateCheckinData()]);
    }
    handlers.onFail();
};

const isRoomColumn = (column: KanbanColumn<ColumnType, DailyAppointment>): boolean =>
    column.type === ColumnType.WAITING_ROOM || column.type === ColumnType.TREATMENT_ROOM;

export const prepareKanbanMove = <T extends ColumnType | DCTaskStatus, K extends { id: string }>(
    event: DragEndEvent,
    filled_columns: Map<string, KanbanColumn<T, K>>
): KanbanMove<T, K> | undefined => {
    const { active, over } = event;
    if (!over) return undefined;

    const active_id = active.id as string;
    const over_id = over.id as string;

    // Find source column and item
    const source_column = [...filled_columns.values()].find((col) => col.items.some((app) => app.id === active_id));
    if (!source_column) return undefined;

    const item = source_column.items.find((app) => app.id === active_id);
    if (!item) return undefined;

    // Find destination column - either by column id or by item id
    const destination_column =
        filled_columns.get(over_id) || [...filled_columns.values()].find((col) => col.id === over_id);
    if (!destination_column) return undefined;

    // If setting in the same column don't change
    if (source_column.id === destination_column.id) return undefined;

    return { item, source_column, destination_column };
};

export enum InvalidMoveReason {
    INSTANCE_ID_MISMATCH = 'INSTANCE_ID_MISMATCH',
    UNREGISTERED_DATA_CUR_APPOINTMENT = 'UNREGISTERED_DATA_CUR_APPOINTMENT',
}

export const isValidMove = (
    destination_column: KanbanColumn<ColumnType, DailyAppointment>,
    appointment: DailyAppointment,
    match_instance_id: boolean = true
): [boolean, InvalidMoveReason | undefined] => {
    // If not instance id specific, it is valid
    if (!match_instance_id) return [true, undefined];

    // Unregistered data cur appointment can't be moved to instance specific column
    if (appointment?.pvs_provider === PVSProvider.DATA_CUR && !isEternoAppointment(appointment)) {
        return [false, InvalidMoveReason.UNREGISTERED_DATA_CUR_APPOINTMENT];
    }

    // Instance id needs to match
    if (destination_column.instance_id !== getInstanceId(appointment)) {
        return [false, InvalidMoveReason.INSTANCE_ID_MISMATCH];
    }

    return [true, undefined];
};

const validateMoveAndNotify = (
    handlers: DragAndDropHandlers,
    destination_column: KanbanColumn<ColumnType, DailyAppointment>,
    appointment: DailyAppointment
): boolean => {
    const [is_valid_move, invalid_move_reason] = isValidMove(destination_column, appointment);

    if (!is_valid_move) {
        switch (invalid_move_reason) {
            case InvalidMoveReason.INSTANCE_ID_MISMATCH: {
                showErrorNotification('Termin und Raum gehören zu unterschiedlichen Instanzen', 'Ungültiger Zug'); // Invalid move: Appointment and room belong to different instances
                break;
            }
            case InvalidMoveReason.UNREGISTERED_DATA_CUR_APPOINTMENT: {
                showErrorNotification('Raum wird bei Data Cur Termin nicht unterstützt.', 'Ungültiger Zug'); // Invalid move: Room is not supported for Data Cur appointment
                break;
            }
            default: {
                showErrorNotification('', 'Ungültiger Zug');
                break;
            }
        }

        handlers.onFail();
    }

    return is_valid_move;
};
